/**
 * One session id + one attribution snapshot for every tracker.
 *
 * Oct 2026 — pageviews, server-side funnel events and checkout all share
 * `tbb_session_id` so the admin funnel can join stages per session.
 * Attribution (UTM params + fbclid/gclid + landing path + referrer) is
 * captured ONCE on the first page of the session and reused, so a shopper
 * who lands from a Meta ad and browses five pages stays "facebook (paid)".
 */
const SESSION_KEY = "tbb_session_id";
const ATTR_KEY = "tbb_attribution";

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

let memorySession = null;

export function getSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = uuid();
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    if (!memorySession) memorySession = uuid();
    return memorySession;
  }
}

function captureFromUrl() {
  if (typeof window === "undefined") return null;
  const out = {};
  try {
    const p = new URLSearchParams(window.location.search);
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
      const v = (p.get(k) || "").trim();
      if (v) out[k] = v.slice(0, 200);
    }
    if (p.get("fbclid")) out.fbclid = true;
    if (p.get("gclid")) out.gclid = true;
  } catch { /* malformed URL — ignore */ }
  out.landing_path = window.location.pathname;
  out.referrer = (typeof document !== "undefined" && document.referrer) ? document.referrer.slice(0, 512) : "";
  return out;
}

let memoryAttr = null;

/** Attribution captured on the session's first pageview. If the current
 *  URL carries UTM params later in the session (new ad click in the same
 *  tab) they overwrite the stored snapshot — last click wins. */
export function getAttribution() {
  const fromUrl = captureFromUrl();
  const hasUtm = fromUrl && (fromUrl.utm_source || fromUrl.fbclid || fromUrl.gclid);
  try {
    const stored = sessionStorage.getItem(ATTR_KEY);
    if (stored && !hasUtm) return JSON.parse(stored);
    const next = fromUrl || {};
    sessionStorage.setItem(ATTR_KEY, JSON.stringify(next));
    return next;
  } catch {
    if (!memoryAttr || hasUtm) memoryAttr = fromUrl || {};
    return memoryAttr;
  }
}

/** Flat blob for the checkout payload — session + attribution together. */
export function checkoutAttribution() {
  return { session_id: getSessionId(), ...(getAttribution() || {}) };
}

/* ── Internal traffic (Oct 2026) ──────────────────────────────────────
 * Admin logins, /admin visits and preview/localhost hosts are "internal":
 * Clarity isn't loaded for them (or gets role=admin), and every beacon
 * carries `internal: true` so the server-side funnel can exclude them.
 * Sticky for the whole tab session so an admin who then browses the
 * shop as a customer doesn't inflate the funnel. */
const INTERNAL_KEY = "tbb_internal";
let memoryInternal = false;

export function isPreviewHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h.endsWith(".preview.emergentagent.com") || h.endsWith(".emergent.host");
}

export function markInternal(reason = "admin") {
  memoryInternal = true;
  try { sessionStorage.setItem(INTERNAL_KEY, reason); } catch { /* private mode */ }
}

export function isInternalSession() {
  if (memoryInternal) return true;
  try { return !!sessionStorage.getItem(INTERNAL_KEY); } catch { return false; }
}
